/**
 * Pure helpers behind DiffViewer: side-by-side row pairing and a light syntax highlighter.
 *
 * The highlighter is deliberately small ("syntax-light"): per line, no parser, no state
 * across lines. It colours keywords, strings, numbers and line comments for common
 * languages, so a diff reads faster without pulling a grammar library into the bundle.
 */
import type { DiffHunk, DiffLine } from '@orgus/protocol';

// ---------------------------------------------------------------------------
// Side by side
// ---------------------------------------------------------------------------

export interface SplitRow {
  left: DiffLine | null;
  right: DiffLine | null;
}

/**
 * Pair a hunk's lines for a side-by-side view: context on both sides; within each run of
 * changes, the n-th deleted line sits beside the n-th added line; leftovers face a blank.
 */
export function splitRows(h: Pick<DiffHunk, 'lines'>): SplitRow[] {
  const rows: SplitRow[] = [];
  let dels: DiffLine[] = [];
  let adds: DiffLine[] = [];
  const flush = () => {
    const n = Math.max(dels.length, adds.length);
    for (let i = 0; i < n; i++) rows.push({ left: dels[i] ?? null, right: adds[i] ?? null });
    dels = [];
    adds = [];
  };
  for (const l of h.lines) {
    if (l.kind === 'del') {
      if (adds.length) flush();
      dels.push(l);
    } else if (l.kind === 'add') adds.push(l);
    else {
      flush();
      rows.push(l.kind === 'note' ? { left: null, right: l } : { left: l, right: l });
    }
  }
  flush();
  return rows;
}

/** Lines a file's diff renders (for collapse-by-default decisions). */
export function diffLineCount(hunks: readonly Pick<DiffHunk, 'lines'>[]): number {
  let n = 0;
  for (const h of hunks) n += h.lines.length;
  return n;
}

// ---------------------------------------------------------------------------
// Syntax-light highlighting
// ---------------------------------------------------------------------------

export type TokenKind = 'kw' | 'str' | 'num' | 'com' | 'plain';
export interface Token {
  text: string;
  kind: TokenKind;
}

interface Lang {
  keywords: ReadonlySet<string>;
  /** Line comment starters, longest first. */
  comments: readonly string[];
  /** Quote characters that open strings. */
  quotes: string;
}

const words = (s: string): ReadonlySet<string> => new Set(s.split(/\s+/).filter(Boolean));

const C_LIKE = 'if else for while do switch case default break continue return function const let var new class extends import export from as async await try catch finally throw typeof instanceof in of void delete this super null undefined true false interface type enum implements public private protected readonly static yield get set';
const LANGS: Record<string, Lang> = {
  js: { keywords: words(C_LIKE), comments: ['//'], quotes: `'"\`` },
  py: {
    keywords: words('def class if elif else for while return import from as with try except finally raise lambda yield pass break continue and or not in is None True False async await global nonlocal self'),
    comments: ['#'],
    quotes: `'"`,
  },
  go: {
    keywords: words('func package import var const type struct interface map chan go defer select switch case default if else for range return break continue fallthrough goto nil true false'),
    comments: ['//'],
    quotes: '"`\'',
  },
  rs: {
    keywords: words('fn let mut pub struct enum impl trait use mod crate self super match if else for while loop return break continue as where move ref const static unsafe async await dyn true false None Some Ok Err'),
    comments: ['//'],
    quotes: '"',
  },
  sh: { keywords: words('if then else elif fi for in do done while case esac function return export local set echo exit'), comments: ['#'], quotes: `'"` },
  css: { keywords: words('important media supports import'), comments: ['//'], quotes: `'"` },
  json: { keywords: words('true false null'), comments: [], quotes: '"' },
  yaml: { keywords: words('true false null yes no'), comments: ['#'], quotes: `'"` },
  sql: {
    keywords: words('select from where insert into update delete create table alter drop join left right inner outer on group by order having limit and or not null as values set index primary key'),
    comments: ['--'],
    quotes: `'"`,
  },
  java: { keywords: words(`${C_LIKE} package final abstract synchronized volatile transient native boolean int long double float char byte short`), comments: ['//'], quotes: `'"` },
};

const EXT: Record<string, string> = {
  ts: 'js', tsx: 'js', js: 'js', jsx: 'js', mjs: 'js', cjs: 'js', mts: 'js', cts: 'js', vue: 'js', svelte: 'js',
  py: 'py', go: 'go', rs: 'rs',
  sh: 'sh', bash: 'sh', zsh: 'sh', ps1: 'sh',
  css: 'css', scss: 'css', less: 'css',
  json: 'json', jsonc: 'json', jsonl: 'json',
  yml: 'yaml', yaml: 'yaml', toml: 'yaml',
  sql: 'sql',
  java: 'java', kt: 'java', cs: 'java', c: 'java', h: 'java', cpp: 'java', hpp: 'java', swift: 'java', dart: 'java', scala: 'java',
};

/** The highlighter language for a path, or null for plain text (markdown, unknown). */
export function langOf(path: string): string | null {
  const name = path.replace(/\\/g, '/').split('/').pop() ?? '';
  if (/^(Dockerfile|Makefile)$/i.test(name)) return 'sh';
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  return EXT[ext] ?? null;
}

const IDENT = /[A-Za-z_$][\w$]*/y;
const NUMBER = /(?:0[xX][\da-fA-F_]+|\d[\d_]*(?:\.\d+)?(?:[eE][+-]?\d+)?)\b/y;

/** Split one line into coloured tokens. Plain text returns one plain token. */
export function highlightLine(text: string, lang: string | null): Token[] {
  const l = lang ? LANGS[lang] : undefined;
  if (!l || !text) return [{ text, kind: 'plain' }];
  const out: Token[] = [];
  let plain = '';
  const emit = (t: string, kind: TokenKind) => {
    if (kind === 'plain') {
      plain += t;
      return;
    }
    if (plain) out.push({ text: plain, kind: 'plain' });
    plain = '';
    out.push({ text: t, kind });
  };
  let i = 0;
  while (i < text.length) {
    const c = text[i] as string;
    const com = l.comments.find((s) => text.startsWith(s, i));
    if (com && (com !== '#' || i === 0 || /\s/.test(text[i - 1] as string))) {
      emit(text.slice(i), 'com');
      break;
    }
    if (l.quotes.includes(c)) {
      let j = i + 1;
      while (j < text.length && text[j] !== c) j += text[j] === '\\' ? 2 : 1;
      emit(text.slice(i, Math.min(text.length, j + 1)), 'str');
      i = j + 1;
      continue;
    }
    if (/\d/.test(c) && !/[\w$]/.test(text[i - 1] ?? ' ')) {
      NUMBER.lastIndex = i;
      const m = NUMBER.exec(text);
      if (m) {
        emit(m[0], 'num');
        i += m[0].length;
        continue;
      }
    }
    if (/[A-Za-z_$]/.test(c)) {
      IDENT.lastIndex = i;
      const m = IDENT.exec(text);
      const w = m ? m[0] : c;
      const kw = lang === 'sql' ? l.keywords.has(w.toLowerCase()) : l.keywords.has(w);
      emit(w, kw ? 'kw' : 'plain');
      i += w.length;
      continue;
    }
    emit(c, 'plain');
    i++;
  }
  if (plain) out.push({ text: plain, kind: 'plain' });
  return out;
}
